import { Input } from "@frui.ts/bootstrap";
import type { ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import DateRangeInputControl from "controls/dateRangeInput";
import AccountNumberOrIbanAndBankCodeInput from "controls/finance/accountNumberOrIbanAndBankCodeInput";
import LicensePlateInput from "controls/licensePlateInput";
import NumberRangeInput from "controls/numberRangeInput";
import { translateGeneral } from "utils/localizations";
import type BankTransferViewModel from "viewModels/finance/outgoingPayments/bankTransferViewModel";
import FilterView from "views/common/filter";
import CountrySelect from "views/common/countrySelect";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { onNumberInputKeyPress } from "../../../../utils/numberInput";
import AdvancedInput from "@edaz/edazkit/controls/AdvancedInput";

const BankTransferFilters: ViewComponent<BankTransferViewModel> = observer(({ vm }) => {
  const fullTextClassNames = vm.filter.fullTextSearchEnabled ? "border-success border-2" : "";
  const guidRegex = new RegExp(/[^A-F0-9-]+/gi);

  return (
    <FilterView vm={vm}>
      <div className="row">
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateModelAttribute("licensePlate", "countryId")}</Form.Label>
          <CountrySelect
            target={vm.filter}
            property="countryId"
            vm={vm.countrySelectViewModel}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translate("licensePlate")}</Form.Label>
          <LicensePlateInput
            className={fullTextClassNames}
            target={vm.filter}
            property="licensePlate"
            placeholder={translateGeneral("search")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={8} xl={6}>
          <Form.Label>{translateGeneral("bankAccountNumber")}</Form.Label>
          <AccountNumberOrIbanAndBankCodeInput
            className={fullTextClassNames}
            target={vm}
            property="filter"
            placeholder={translateGeneral("search")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translate("variableSymbolIncomingAndOutgoingPayment")}</Form.Label>
          <Input
            target={vm.filter}
            property="variableSymbol"
            placeholder={translateGeneral("search")}
            type="number"
            onKeyDown={onNumberInputKeyPress}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateAttribute("paidAmount")}</Form.Label>
          <NumberRangeInput target={vm.filter} property="paidAmount" min={0} step={0.01} />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={8} xl={6}>
          <Form.Label>{vm.translateAttribute("postingDate")}</Form.Label>
          <DateRangeInputControl target={vm.filter} property="postingDate" />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={8} xl={6}>
          <Form.Label>{vm.translateAttribute("exportedDate")}</Form.Label>
          <DateRangeInputControl target={vm.filter} property="exportedDate" />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translate("officialRecordId")}</Form.Label>
          <Input
            target={vm.filter}
            className={fullTextClassNames}
            property="officialRecordId"
            placeholder={translateGeneral("search")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateAttribute("messageForRecipient")}</Form.Label>
          <Input target={vm.filter} property="messageForRecipient" placeholder={translateGeneral("search")} />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateAttribute("status")}</Form.Label>
          <AdvancedSelect
            target={vm.filter}
            property="status"
            items={vm.statusOptions}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translate("chargeOrderId")}</Form.Label>
          <AdvancedInput
            target={vm.filter}
            property="chargeOrderId"
            placeholder={translateGeneral("search")}
            ignoredCharacters={guidRegex}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translate("chargeId")}</Form.Label>
          <AdvancedInput
            target={vm.filter}
            property="chargeId"
            placeholder={translateGeneral("search")}
            ignoredCharacters={guidRegex}
          />
        </Form.Group>
      </div>
    </FilterView>
  );
});

export default BankTransferFilters;
